import { useEffect, useRef, useState } from "react";
import {
  FINISH_MOVE_LINE, REPLY_URL_PENDING, REPLY_URL_POLL_MS, REPLY_URL_STOPPED, SIGN_IN_APP_LABELS, consentLinkLabel,
  mideyeConsentLine, moveToMideyeGate, moveView, movingHeading, panelTarget, replyUrlPollDue, signInPanelCopy as copy,
  signInTargets,
} from "../lib/eam";
import type { EamRealm, EamSignInApp } from "../types/api";
import OwnAppSteps from "./OwnAppSteps";

const PRIMARY_BUTTON =
  "inline-flex items-center px-5 py-2.5 border border-transparent rounded-lg shadow-subtle text-sm font-medium text-white bg-accent hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
const SECONDARY_BUTTON =
  "inline-flex items-center px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent transition-colors";
const TEXT_BUTTON = "py-2 text-sm font-medium text-gray-600 hover:text-gray-800 disabled:opacity-50";
const CARD = "bg-white rounded-xl shadow-card border border-gray-100 p-6";
const ROW = "mt-4 flex flex-wrap items-center justify-end gap-3";

interface Props {
  realm: EamRealm;
  redirectUri: string | undefined;
  mideyeAppConsentUrl: string | null | undefined;
  /** The admin's pick, else the target the panel opened on ("Change the ID" opens it on the own app). */
  pick: EamSignInApp | null;
  onPick: (target: EamSignInApp) => void;
  busy: boolean;
  onSaveOwnApp: (appId: string) => Promise<string | null>;
  onSaveMideye: () => void;
  onFinish: (appId: string) => void;
  onCancelMove: (appId: string) => void;
  onClose: () => void;
  /** Reloads the view; the panel calls it while it waits for the reply URL. */
  onReload: () => void;
  /** True when the admin opened the panel: it then moves focus to its heading. */
  takeFocus: boolean;
}

/** Reloads every few seconds, for a limited time, while the reply URL is not registered. */
function ReplyUrlWait({ onReload }: { onReload: () => void }) {
  const [stopped, setStopped] = useState(false);
  // Each reload re-renders the page with a new callback; the timer must not restart with it.
  const reload = useRef(onReload);
  useEffect(() => {
    reload.current = onReload;
  });
  useEffect(() => {
    const start = Date.now();
    const timer = setInterval(() => {
      if (replyUrlPollDue(Date.now() - start)) {
        reload.current();
      } else {
        clearInterval(timer);
        setStopped(true);
      }
    }, REPLY_URL_POLL_MS);
    return () => clearInterval(timer);
  }, []);

  return (
    <p className="mt-3 text-sm text-gray-500" role="status">
      {REPLY_URL_PENDING}
      {stopped && ` ${REPLY_URL_STOPPED}`}
    </p>
  );
}

export default function SignInAppPanel(props: Props) {
  const { realm, busy } = props;
  const move = moveView(realm);
  const targets = signInTargets(realm);
  const [acting, setActing] = useState<"finish" | "cancel" | null>(null);
  const target = panelTarget(targets, props.pick);
  const heading = useRef<HTMLHeadingElement>(null);
  const { takeFocus } = props;

  useEffect(() => {
    if (takeFocus) heading.current?.focus();
  }, [takeFocus]);

  if (move) {
    return (
      <div className={`${CARD} mb-6`}>
        <h3 ref={heading} tabIndex={-1} className="text-sm font-medium text-gray-700 focus:outline-none">
          {movingHeading(realm)}
        </h3>
        <p className="mt-2 text-sm text-gray-700">
          {copy.newAppId} <span className="break-all font-mono">{move.appId}</span>
        </p>
        <p className="mt-2 text-sm text-gray-500">{FINISH_MOVE_LINE}</p>
        <div className={ROW}>
          <button
            className={TEXT_BUTTON}
            disabled={busy}
            onClick={() => {
              setActing("cancel");
              props.onCancelMove(move.appId);
            }}
          >
            {busy && acting === "cancel" ? copy.cancelling : copy.cancelMove}
          </button>
          <button
            className={PRIMARY_BUTTON}
            disabled={busy}
            onClick={() => {
              setActing("finish");
              props.onFinish(move.appId);
            }}
          >
            {busy && acting === "finish" ? copy.finishing : copy.finishMove}
          </button>
        </div>
      </div>
    );
  }

  if (targets.length === 0) return null;
  const gate = moveToMideyeGate(realm, props.mideyeAppConsentUrl);
  // type="button": inside the own app's form, a plain button would submit the ID.
  const closeButton = (
    <button type="button" className={TEXT_BUTTON} onClick={props.onClose}>
      {copy.close}
    </button>
  );

  return (
    <div className={`${CARD} mb-6`}>
      <h3 ref={heading} tabIndex={-1} className="text-sm font-medium text-gray-700 focus:outline-none">
        {copy.heading}
      </h3>
      {targets.length > 1 && (
        <fieldset className="mt-2 space-y-2 text-sm">
          <legend className="sr-only">{copy.targetLegend}</legend>
          {targets.map((t) => (
            <label key={t} className="flex items-center gap-2">
              <input type="radio" name="signInAppTarget" checked={target === t} onChange={() => props.onPick(t)} />
              <b>{t === "customer" ? copy.ownAppTarget : SIGN_IN_APP_LABELS[t]}</b>
            </label>
          ))}
        </fieldset>
      )}
      {targets.length === 1 && target && (
        <p className="mt-2 text-sm text-gray-700">
          {copy.moveTo} <b>{SIGN_IN_APP_LABELS[target]}</b>
        </p>
      )}

      {target === "customer" && (
        <div className="mt-4">
          <OwnAppSteps
            redirectUri={props.redirectUri}
            tenantId={realm.tenant_id}
            phoneSource={realm.phone_source}
            onSave={props.onSaveOwnApp}
            busy={busy}
            besideSave={closeButton}
          />
        </div>
      )}
      {target === "mideye" && (
        <>
          {gate.consentUrl && <p className="mt-4 text-sm text-gray-500">{mideyeConsentLine(realm)}</p>}
          {gate.waitingForReplyUrl && <ReplyUrlWait onReload={props.onReload} />}
          <div className={ROW}>
            {closeButton}
            {gate.consentUrl && (
              <a href={gate.consentUrl} target="_blank" rel="noopener noreferrer" className={SECONDARY_BUTTON}>
                {consentLinkLabel(realm)}
              </a>
            )}
            <button className={PRIMARY_BUTTON} disabled={busy || gate.waitingForReplyUrl} onClick={props.onSaveMideye}>
              {busy ? copy.saving : copy.save}
            </button>
          </div>
        </>
      )}
      {target === null && (
        <div className={ROW}>
          {closeButton}
          <button className={PRIMARY_BUTTON} disabled>
            {copy.save}
          </button>
        </div>
      )}
    </div>
  );
}
